import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import {
  ArrowLeft, Voicemail as VoicemailIcon, Upload, Trash2, Mic, MicOff,
  PlayCircle, PauseCircle, Inbox, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { usePageHeader } from '../context/PageHeaderContext';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import PullToRefreshIndicator from '../components/PullToRefreshIndicator';

/**
 * /voicemail — available to ALL users.
 *
 * Sections:
 *   1. Greeting — toggle between System Default and Custom (upload or record).
 *   2. Inbox  — list of voicemails left for the current user, with playback.
 */
const VoicemailPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { api } = useAuth();
  usePageHeader({ title: 'Voicemail' }, []);
  const [greeting, setGreeting] = useState(null);
  const [inbox, setInbox] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const fileInputRef = useRef(null);
  const audioRef = useRef(null);
  const inboxAudioRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const highlightedOnceRef = useRef(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  const load = async () => {
    try {
      const [g, i] = await Promise.all([
        api.get('/voicemail/greeting'),
        api.get('/voicemail/inbox'),
      ]);
      setGreeting(g.data);
      setInbox(i.data?.items || []);
    } catch (e) {
      toast.error('Could not load voicemail');
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Pull-to-refresh — page uses document scroll.
  const { pullY, refreshing } = usePullToRefresh({ onRefresh: load });

  // Deep-link: ?vm=<id> → scroll to + auto-play that voicemail once the
  // inbox has loaded. Invoked from the in-app missed-call /
  // voicemail notifications so users land precisely on the
  // message they tapped.
  useEffect(() => {
    const vmId = searchParams.get('vm');
    if (!vmId || highlightedOnceRef.current || inbox.length === 0) return;
    const target = inbox.find((v) => v.id === vmId);
    if (!target) return;
    highlightedOnceRef.current = true;
    setHighlightId(vmId);
    // Scroll + auto-play after the DOM paints.
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="voicemail-item-${vmId}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Trigger play via the existing handler (handles the Twilio proxy auth).
      try { playInbox(target); } catch (e) { /* ignore */ }
    }, 150);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inbox, searchParams]);

  const setMode = async (mode) => {
    try {
      await api.put('/voicemail/greeting', { mode });
      await load();
      toast.success(mode === 'custom' ? 'Using your custom greeting' : 'Using system default greeting');
    } catch (e) {
      toast.error('Could not update greeting mode');
    }
  };

  const onSelectFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', f);
      await api.post('/voicemail/greeting/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      await load();
      toast.success('Custom greeting saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onDeleteCustom = async () => {
    if (!window.confirm('Remove your custom greeting? You will fall back to the system default.')) return;
    try {
      await api.delete('/voicemail/greeting/custom');
      await load();
      toast.success('Custom greeting removed');
    } catch (e) {
      toast.error('Could not delete');
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      recordedChunksRef.current = [];
      rec.ondataavailable = (ev) => { if (ev.data?.size > 0) recordedChunksRef.current.push(ev.data); };
      rec.onstop = async () => {
        try {
          const blob = new Blob(recordedChunksRef.current, { type: 'audio/webm' });
          const fd = new FormData();
          fd.append('file', blob, 'greeting.webm');
          setUploading(true);
          await api.post('/voicemail/greeting/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          await load();
          toast.success('Greeting recorded and saved');
        } catch (e) {
          toast.error('Could not save recording');
        } finally {
          stream.getTracks().forEach((t) => t.stop());
          setUploading(false);
        }
      };
      mediaRecorderRef.current = rec;
      rec.start();
      setRecording(true);
      // Safety auto-stop after 30 seconds.
      setTimeout(() => { if (rec.state === 'recording') rec.stop(); setRecording(false); }, 30000);
    } catch (e) {
      toast.error('Microphone access denied');
    }
  };

  const stopRecording = () => {
    try { mediaRecorderRef.current?.stop(); } catch (e) {}
    setRecording(false);
  };

  const togglePreview = () => {
    if (!greeting?.effective_greeting_url) return;
    const url = `${backendUrl}${greeting.effective_greeting_url}`;
    if (audioRef.current) {
      try { audioRef.current.pause(); audioRef.current.src = ''; audioRef.current = null; } catch (e) {}
      setPreviewUrl(null);
      return;
    }
    const a = new Audio(url);
    a.onended = () => { audioRef.current = null; setPreviewUrl(null); };
    a.play().then(() => { audioRef.current = a; setPreviewUrl(url); }).catch(() => {});
  };

  const playInbox = async (vm) => {
    if (playingId === vm.id) {
      try { inboxAudioRef.current?.pause(); inboxAudioRef.current = null; } catch (e) {}
      setPlayingId(null);
      return;
    }
    try { inboxAudioRef.current?.pause(); } catch (e) {}
    // Fetch via authenticated proxy so Twilio-hosted voicemails
    // (which require AccountSid+AuthToken) play. Internal voicemails
    // are served from our own storage through the same endpoint.
    try {
      const resp = await fetch(`${backendUrl}/api/voicemail/${vm.id}/audio`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('orb_token') || ''}` },
      });
      if (!resp.ok) {
        toast.error(`Could not load voicemail (HTTP ${resp.status})`);
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = new Audio(url);
      a.onended = () => { inboxAudioRef.current = null; setPlayingId(null); };
      await a.play();
      inboxAudioRef.current = a;
      setPlayingId(vm.id);
      if (!vm.read_at) {
        try { await api.post(`/voicemail/${vm.id}/mark-read`); load(); } catch (e) {}
      }
    } catch (e) {
      toast.error(e?.message || 'Could not play voicemail');
    }
  };

  const onDeleteVoicemail = async (vm) => {
    if (!window.confirm('Delete this voicemail?')) return;
    try { await api.delete(`/voicemail/${vm.id}`); load(); toast.success('Voicemail deleted'); } catch (e) { toast.error('Could not delete'); }
  };

  const isCustom = greeting?.mode === 'custom' && greeting?.custom_greeting_url;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] pb-24" data-testid="voicemail-page">
      <PullToRefreshIndicator pullY={pullY} refreshing={refreshing} />
      <div className="max-w-2xl mx-auto p-3">
        {/* Greeting */}
        <Card className="rounded-2xl mb-4">
          <CardHeader>
            <CardTitle className="text-lg">Greeting</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between rounded-xl border border-gray-200 dark:border-gray-700 p-3 bg-white/60 dark:bg-[#1a1a1a]/60">
              <div className="pr-3">
                <p className="font-medium">Use my custom greeting</p>
                <p className="text-xs text-muted-foreground">When off, callers hear the system default.</p>
              </div>
              <Switch
                checked={greeting?.mode === 'custom'}
                onCheckedChange={(v) => setMode(v ? 'custom' : 'system')}
                disabled={!greeting?.custom_greeting_url && greeting?.mode !== 'custom'}
                data-testid="voicemail-greeting-mode-toggle"
              />
            </div>

            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 bg-white/60 dark:bg-[#1a1a1a]/60">
              <p className="text-sm font-medium mb-1">
                {isCustom ? 'Your custom greeting' : 'System default greeting'}
              </p>
              <p className="text-xs text-muted-foreground mb-3 truncate">
                {isCustom ? (greeting?.custom_greeting_filename || 'custom greeting') : 'Default Voicemail.mp3'}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={togglePreview} data-testid="voicemail-preview">
                  {previewUrl ? <PauseCircle className="w-4 h-4 mr-1.5" /> : <PlayCircle className="w-4 h-4 mr-1.5" />}
                  {previewUrl ? 'Pause' : 'Preview'}
                </Button>
                {!recording ? (
                  <Button size="sm" variant="outline" onClick={startRecording} disabled={uploading} data-testid="voicemail-record-start">
                    <Mic className="w-4 h-4 mr-1.5" />
                    Record new
                  </Button>
                ) : (
                  <Button size="sm" variant="destructive" onClick={stopRecording} data-testid="voicemail-record-stop">
                    <MicOff className="w-4 h-4 mr-1.5" />
                    Stop & save
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="voicemail-upload">
                  <Upload className="w-4 h-4 mr-1.5" />
                  Upload audio
                </Button>
                {greeting?.custom_greeting_url && (
                  <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={onDeleteCustom} data-testid="voicemail-delete-custom">
                    <Trash2 className="w-4 h-4 mr-1.5" />
                    Remove custom
                  </Button>
                )}
              </div>
              <input ref={fileInputRef} type="file" accept="audio/mpeg,audio/mp3,audio/m4a,audio/wav,audio/ogg,audio/aac,.mp3,.m4a,.wav,.ogg,.aac" hidden onChange={onSelectFile} data-testid="voicemail-greeting-file-input" />
            </div>
          </CardContent>
        </Card>

        {/* Inbox */}
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Inbox className="w-5 h-5 text-[#586D52]" />
              Inbox
              <span className="text-xs text-muted-foreground ml-2">{inbox.length} message{inbox.length === 1 ? '' : 's'}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {inbox.length === 0 ? (
              <div className="text-center text-sm text-muted-foreground py-8">No voicemails yet.</div>
            ) : (
              <div className="space-y-2">
                {inbox.map((vm) => (
                  <div
                    key={vm.id}
                    className={`rounded-xl border p-3 flex items-center justify-between gap-2 ${vm.read_at ? 'bg-white/40 dark:bg-[#1a1a1a]/40 border-gray-200 dark:border-gray-700' : 'bg-[#586D52]/10 border-[#586D52]/40'} ${highlightId === vm.id ? 'ring-2 ring-[#586D52] animate-pulse' : ''}`}
                    data-testid={`voicemail-item-${vm.id}`}
                  >
                    <button onClick={() => playInbox(vm)} className="flex items-center gap-3 flex-1 text-left" data-testid={`voicemail-play-${vm.id}`}>
                      {playingId === vm.id ? <PauseCircle className="w-7 h-7 text-[#586D52] flex-shrink-0" /> : <PlayCircle className="w-7 h-7 text-[#586D52] flex-shrink-0" />}
                      <div className="min-w-0">
                        <p className="font-medium truncate">{vm.sender_name || 'Unknown'}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                          <Clock className="w-3 h-3" />
                          {new Date(vm.created_at).toLocaleString()}
                          {vm.duration_sec ? <span>· {Math.round(vm.duration_sec)}s</span> : null}
                          {!vm.read_at && <span className="ml-1 inline-block w-2 h-2 rounded-full bg-red-500" />}
                        </p>
                      </div>
                    </button>
                    <Button size="icon" variant="ghost" className="text-red-600" onClick={() => onDeleteVoicemail(vm)} data-testid={`voicemail-delete-${vm.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VoicemailPage;
